/**
 * AreaCommandChildrenSection (R5 Slice 3, #507) — the Area Command parent's expand
 * panel: the child incidents grouped under this Area Command (Ontario IMS §4.6 /
 * App A #2; the #493 children[] rollup).
 *
 * Gate-corrected vocabulary (2026-07-02 IMS design gate, 4-seat panel unanimous):
 *   - Heading: "Incidents under area command (N)" — "under area command" is VERBATIM
 *     Ontario IMS phrasing (App A #85; grouping concept §4.6 / App A #2); the children
 *     are App A #35 INCIDENTS, never "members" ("member(s)" appears
 *     nowhere in §4/App A and collides with App A #85 unified area command + App A #39
 *     IMT personnel).
 *   - Each child row renders the child's OWN incident name + incident_number ONLY —
 *     NEVER FIRE_COMPLEX_NAME or any foreign/backend value (the #497 raw-CAP sentinel
 *     rule; pinned by areaCommandChildren.lock.test.tsx).
 *   - NO person field renders here (App A #2 — area command is a FUNCTION of the
 *     participating organizations; Ontario titles no "Area Commander", #498). Each
 *     child keeps its OWN Incident Command block on its own panel, one click away.
 *
 * Extracted from IncidentManagementSection so the vocabulary lock can render this
 * surface directly with sentinel children (the #499 rendered-strings pattern).
 */
import type { JSX } from 'react';
import type { Incident } from '@tsr/shared-types';
import {
  NO_INCIDENTS_GROUPED_EMPTY,
  REMOVE_FROM_AREA_COMMAND_LABEL,
  incidentsUnderAreaCommandHeading,
} from '../incidentVocabulary';
import { hazardStyle } from '../styles/incidentStyles';

export interface AreaCommandChildrenSectionProps {
  /** The parent's children[] rollup (#493 IncidentDetail) — the semantic membership.
   *  Named childIncidents (not `children`) — they are App A #35 incidents, and the
   *  React children prop is reserved. */
  childIncidents: ReadonlyArray<Incident>;
  busy: boolean;
  /** Remove a child from the Area Command (reversible, §4.6 oversight only). */
  onRemove: (childIncidentId: string) => void;
}

export function AreaCommandChildrenSection({
  childIncidents,
  busy,
  onRemove,
}: AreaCommandChildrenSectionProps): JSX.Element {
  return (
    <div
      data-testid="area-command-members"
      className="rounded border border-indigo-500/40 bg-indigo-500/5 px-3 py-2"
    >
      <h4 className="text-[10px] uppercase tracking-wider text-indigo-300">
        {incidentsUnderAreaCommandHeading(childIncidents.length)}
      </h4>
      {childIncidents.length === 0 ? (
        <p className="mt-1 text-[11px] text-slate-500" data-testid="area-command-empty">
          {NO_INCIDENTS_GROUPED_EMPTY}
        </p>
      ) : (
        <ul className="mt-1 space-y-1">
          {childIncidents.map((c) => {
            const chz = hazardStyle(c.hazard_type);
            return (
              <li key={c.id} data-testid="area-command-member" className="flex items-center gap-2">
                <span className="shrink-0 text-[10px]" style={{ color: chz.color }} aria-hidden>
                  {chz.icon}
                </span>
                {/* The child's OWN name + incident_number — the ONLY identity fields
                    rendered (never FIRE_COMPLEX_NAME / any foreign value, #497 rule). The
                    name click-throughs to the child incident (#514, C3): #incident=<id>
                    is the shared in-app deep-link the SITREP member rows also use. */}
                <a
                  href={`#incident=${c.id}`}
                  data-testid="area-command-member-open"
                  title={`Open ${c.name}`}
                  className="min-w-0 flex-1 truncate text-[12px] text-sky-300 hover:underline"
                >
                  {c.name}
                </a>
                <span className="shrink-0 font-mono text-[10px] text-slate-500">
                  {c.incident_number}
                </span>
                <button
                  type="button"
                  data-testid="area-command-member-remove"
                  disabled={busy}
                  onClick={() => onRemove(c.id)}
                  className="shrink-0 rounded border border-slate-500/60 px-1.5 py-0.5 text-[10px] text-slate-300 hover:bg-slate-700/40 disabled:opacity-50"
                >
                  {REMOVE_FROM_AREA_COMMAND_LABEL}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
